import { useState, useEffect } from 'react'
import { useMsal } from '@azure/msal-react'
import { getMe } from '../services/api'
import type { Capabilities, Capability, UserProfile } from '../types/portal'

/**
 * App Roles del Portal de Proveedores LeaseMD (definidos en Entra ID).
 *
 * - admin            → control total (api keys, plantillas, guía, entidades, config)
 * - accounts_payable → revisa/aprueba CFDIs, complementos, NC; PATCH estado_cierre;
 *                      reactiva ventanas (solicitar-documento); recordatorio CP;
 *                      CRUD de entidades receptoras.
 * - operations       → crea oportunidades, solicita docs, recordatorios CP, OC manual.
 * - vendor_manager   → aprueba altas de distribuidor + gestiona catálogo.
 */
export type AppRole =
  | 'admin'
  | 'accounts_payable'
  | 'operations'
  | 'vendor_manager'
  | null

/**
 * Capabilities: las decide el BACKEND (`permissions.CAPABILITIES`) y las expone
 * en `GET /api/auth/me`. El frontend ya NO recalcula permisos ni decodifica el
 * JWT — solo consume esto. Fuente única de verdad → sin drift entre capas.
 */
const ALL_CAPABILITIES: Capability[] = [
  'view_dashboard',
  'view_notificaciones',
  'view_oportunidades',
  'create_oportunidad',
  'manage_oc',
  'request_documents',
  'change_estado_cierre',
  'review_cfdi_read',
  'review_cfdi_write',
  'view_complementos',
  'complementos_action',
  'view_pagos',
  'view_proveedores',
  'manage_vendors',
  'manage_entidades',
  'admin_config',
]

// Fail-closed: ante error/carga, TODO en false (nunca abrir UI por defecto).
const ALL_FALSE = Object.fromEntries(
  ALL_CAPABILITIES.map(c => [c, false]),
) as Capabilities

// Cache compartida del perfil: `useRole` se llama en varios componentes
// (Navbar, rutas, cards del detalle…). Sin esto haríamos N llamadas a /me.
// Se invalida cuando cambia la cuenta MSAL (login/logout/switch).
let _meCacheKey: string | null = null
let _mePromise: Promise<UserProfile> | null = null

function fetchMeCached(key: string): Promise<UserProfile> {
  if (_meCacheKey !== key || !_mePromise) {
    _meCacheKey = key
    // Ante error limpiamos la cache para reintentar en el próximo montaje.
    _mePromise = getMe().catch(err => {
      _mePromise = null
      throw err
    })
  }
  return _mePromise
}

function pickPrimaryRole(rawRoles: string[]): AppRole {
  // Si tiene varios roles, elegimos el de mayor prioridad para mostrar en UI.
  // El backend valida cada acción contra el set completo; esto solo afecta display.
  const priority: Record<string, number> = {
    admin: 4,
    accounts_payable: 3,
    operations: 2,
    vendor_manager: 1,
  }
  if (!rawRoles.length) return null
  const top = [...rawRoles].sort(
    (a, b) => (priority[b] ?? -1) - (priority[a] ?? -1),
  )[0]
  if (
    top === 'admin' ||
    top === 'accounts_payable' ||
    top === 'operations' ||
    top === 'vendor_manager'
  ) {
    return top
  }
  return null
}

/**
 * Lee el perfil + capabilities del usuario desde `/api/auth/me`.
 *
 * Returns { role, roles, loading, can }:
 *   - loading: true mientras se resuelve /me.
 *   - role: rol primario (mayor prioridad) para display; null = sin rol.
 *   - roles: array completo de roles.
 *   - can: capabilities calculadas por el backend (fail-closed en error).
 */
export function useRole(): {
  role: AppRole
  roles: string[]
  loading: boolean
  can: Capabilities
} {
  const { accounts } = useMsal()
  const [roles, setRoles] = useState<string[]>([])
  const [can, setCan] = useState<Capabilities>(ALL_FALSE)
  const [loading, setLoading] = useState(true)

  const accountKey = accounts?.[0]?.homeAccountId ?? 'anon'

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    fetchMeCached(accountKey)
      .then(profile => {
        if (cancelled) return
        setRoles(profile.roles ?? [])
        setCan(profile.capabilities ?? ALL_FALSE)
      })
      .catch(() => {
        if (cancelled) return
        // Fail-closed: sin permisos ante 401/red/shape inesperado.
        setRoles([])
        setCan(ALL_FALSE)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
    // Reintenta cuando cambia la cuenta MSAL (login/logout/switch).
  }, [accountKey])

  return { role: pickPrimaryRole(roles), roles, loading, can }
}

/** Returns true if the user has at least one of the allowed roles. */
export function useHasRole(...allowed: AppRole[]): boolean {
  const { role } = useRole()
  return allowed.includes(role)
}
